import { Checkbox, FormControlLabel } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { GetCustomerSettingsResponse, UpdateCustomerSettingsRequest } from '../../../../Generated/Commodity-Management-Api';
import { RootState, useApplicationDispatch, useApplicationSelector } from '../../../../Redux/ReduxStore';
import { setCustomerSettings } from '../../../../Redux/Slices/CommodityManagementSlice';
import { UserAccessState } from '../../../../Redux/Slices/UserAccessSlice';
import { StoneXDivider, StoneXRow } from '../../../StoneX';
import { StoneXButton, StoneXSelect } from '../../../StoneXMui';
import { CommodityManagementApi } from '../../../../Apis/Apis';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function CustomerSettings() {
  const dispatch = useApplicationDispatch();
  const userAccess = useApplicationSelector((state: RootState): UserAccessState => state.userAccess);
  const customerSettings = useApplicationSelector((state: RootState): GetCustomerSettingsResponse => state.commodityManagement.customerSettings);

  const [draftSettings, setDraftSettings] = useState<GetCustomerSettingsResponse>(customerSettings ?? {} as GetCustomerSettingsResponse);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    setDraftSettings(customerSettings ?? {} as GetCustomerSettingsResponse);
  }, [customerSettings]);

  const fiscalMonthOffsetOptions = useMemo(() => {
    return Array.from({ length: 23 }, (_, index) => {
      const value = index - 11;
      const monthIndex = ((value % 12) + 12) % 12;

      return {
        value,
        label: `${value} (${MONTH_NAMES[monthIndex]})`
      };
    });
  }, []);

  const selectedFiscalMonthOffsetOption = useMemo(() => {
    const selectedValue = draftSettings.fiscalMonthOffset ?? 0;
    return fiscalMonthOffsetOptions.find((x) => x.value === selectedValue) ?? null;
  }, [draftSettings.fiscalMonthOffset, fiscalMonthOffsetOptions]);

  function updateBooleanSetting(field: keyof GetCustomerSettingsResponse, value: boolean) {
    setDraftSettings((previous) => ({ ...previous, [field]: value }));
  }

  function resetSettings() {
    setDraftSettings(customerSettings ?? {} as GetCustomerSettingsResponse);
  }

  async function saveSettings() {
    try {
      setIsSaving(true);
      const request: UpdateCustomerSettingsRequest = {
        fiscalMonthOffset: draftSettings.fiscalMonthOffset ?? 0,
        budgetsByLocation: draftSettings.budgetsByLocation ?? false,
        budgetsByCounterparty: draftSettings.budgetsByCounterparty ?? false,
        budgetsByPortfolio: draftSettings.budgetsByPortfolio ?? false,
        positionsByLocation: draftSettings.positionsByLocation ?? false,
        positionsByCounterparty: draftSettings.positionsByCounterparty ?? false,
        positionsByPortfolio: draftSettings.positionsByPortfolio ?? false,
        usesBudgetRounds: draftSettings.usesBudgetRounds ?? false,
        enableLastDayProductTypes: (draftSettings as { enableLastDayProductTypes?: boolean }).enableLastDayProductTypes ?? false,
        separatePricingStrategiesForPositions: draftSettings.separatePricingStrategiesForPositions ?? false,
        positionContractDates: draftSettings.positionContractDates ?? false,
      };

      const response = await CommodityManagementApi.updateCustomerSettings(undefined, request);
      dispatch(setCustomerSettings(response.data));
    } finally {
      setIsSaving(false);
    }
  }

  if (!userAccess.isAdmin) {
    return <p>Admins only.</p>;
  }

  return (
    <div>
      <StoneXRow>
        <StoneXSelect
          label="Fiscal Year Start"
          options={fiscalMonthOffsetOptions}
          getId={(x) => x.value}
          getOptionLabel={(x) => x.label}
          value={selectedFiscalMonthOffsetOption}
          onChange={(value) => setDraftSettings((previous) => ({ ...previous, fiscalMonthOffset: value?.value ?? 0 }))}
          width={220}
        />
      </StoneXRow>

      <StoneXDivider />

      <StoneXRow>
        <FormControlLabel
          control={<Checkbox checked={draftSettings.budgetsByLocation ?? false} onChange={(event) => updateBooleanSetting('budgetsByLocation', event.target.checked)} />}
          label="Budgets by Location"
        />
      </StoneXRow>
      <StoneXRow>
        <FormControlLabel
          control={<Checkbox checked={draftSettings.budgetsByCounterparty ?? false} onChange={(event) => updateBooleanSetting('budgetsByCounterparty', event.target.checked)} />}
          label="Budgets by Counterparty"
        />
      </StoneXRow>
      <StoneXRow>
        <FormControlLabel
          control={<Checkbox checked={draftSettings.budgetsByPortfolio ?? false} onChange={(event) => updateBooleanSetting('budgetsByPortfolio', event.target.checked)} />}
          label="Budgets by Portfolio"
        />
      </StoneXRow>

      <StoneXDivider />

      <StoneXRow>
        <FormControlLabel
          control={<Checkbox checked={draftSettings.positionsByLocation ?? false} onChange={(event) => updateBooleanSetting('positionsByLocation', event.target.checked)} />}
          label="Positions by Location"
        />
      </StoneXRow>
      <StoneXRow>
        <FormControlLabel
          control={<Checkbox checked={draftSettings.positionsByCounterparty ?? false} onChange={(event) => updateBooleanSetting('positionsByCounterparty', event.target.checked)} />}
          label="Positions by Counterparty"
        />
      </StoneXRow>
      <StoneXRow>
        <FormControlLabel
          control={<Checkbox checked={draftSettings.positionsByPortfolio ?? false} onChange={(event) => updateBooleanSetting('positionsByPortfolio', event.target.checked)} />}
          label="Positions by Portfolio"
        />
      </StoneXRow>

      <StoneXDivider />

      <StoneXRow>
        <FormControlLabel
          control={<Checkbox checked={draftSettings.usesBudgetRounds ?? false} onChange={(event) => updateBooleanSetting('usesBudgetRounds', event.target.checked)} />}
          label="Uses Budget Rounds"
        />
      </StoneXRow>
      <StoneXRow>
        <FormControlLabel
          control={<Checkbox checked={draftSettings.separatePricingStrategiesForPositions ?? false} onChange={(event) => updateBooleanSetting('separatePricingStrategiesForPositions', event.target.checked)} />}
          label="Separate Pricing Strategies For Positions"
        />
      </StoneXRow>
      <StoneXRow>
        <FormControlLabel
          control={<Checkbox checked={draftSettings.positionContractDates ?? false} onChange={(event) => updateBooleanSetting('positionContractDates', event.target.checked)} />}
          label="Position Contract Dates"
        />
      </StoneXRow>

      <StoneXRow justify="end">
        <StoneXButton variant="outlined" onClick={resetSettings} disabled={isSaving}>Reset</StoneXButton>
        <StoneXButton onClick={saveSettings} isWorking={isSaving}>Save</StoneXButton>
      </StoneXRow>
    </div>
  );
}
